import { alpha, createTheme } from "@mui/material/styles";

export type AppThemeMode = "light" | "dark";

const getThemeTokens = (mode: AppThemeMode) => {
  if (mode === "light") {
    return {
      bodyBackground:
        "radial-gradient(circle at top left, rgba(77, 208, 225, 0.18), transparent 24%), radial-gradient(circle at top right, rgba(255, 184, 108, 0.14), transparent 22%), linear-gradient(180deg, #f7fbff 0%, #eef5fb 52%, #e6eef8 100%)",
      bodyColor: "#102033",
      defaultBackground: "#eef5fb",
      paperBackground: "#ffffff",
      textPrimary: "#102033",
      textSecondary: "#526174",
      dividerBase: "#102033",
      cardBackground: "#ffffff",
      paperBorderBase: "#102033",
      dialogBackground: "#ffffff",
      inputBackground: "#ffffff",
      outlinedInputBorderBase: "#102033",
      buttonContainedStart: "#4dd0e1",
      buttonContainedEnd: "#2aa6cf",
      buttonContainedHoverStart: "#67dbe9",
      buttonContainedHoverEnd: "#3eb0d6",
      buttonContainedColor: "#081018",
      listItemActiveBackground: alpha("#4dd0e1", 0.14),
    };
  }

  return {
    bodyBackground:
      "radial-gradient(circle at top left, rgba(77, 208, 225, 0.16), transparent 24%), radial-gradient(circle at top right, rgba(255, 184, 108, 0.12), transparent 22%), linear-gradient(180deg, #050816 0%, #0a1020 52%, #0f172a 100%)",
    bodyColor: "#f3f6ff",
    defaultBackground: "#050816",
    paperBackground: "#0d1324",
    textPrimary: "#f3f6ff",
    textSecondary: "#9aa7c2",
    dividerBase: "#b7c3db",
    cardBackground: "#0d1324",
    paperBorderBase: "#b7c3db",
    dialogBackground: "#0d1324",
    inputBackground: "#050816",
    outlinedInputBorderBase: "#b7c3db",
    buttonContainedStart: "#4dd0e1",
    buttonContainedEnd: "#2aa6cf",
    buttonContainedHoverStart: "#67dbe9",
    buttonContainedHoverEnd: "#3eb0d6",
    buttonContainedColor: "#081018",
    listItemActiveBackground: alpha("#4dd0e1", 0.14),
  };
};

export const createAppTheme = (mode: AppThemeMode = "dark") => {
  const tokens = getThemeTokens(mode);

  return createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
    palette: {
      mode,
      primary: {
        main: "#4dd0e1",
        light: "#88ffff",
        dark: "#009faf",
        contrastText: "#081018",
      },
      secondary: {
        main: "#ffb86c",
        light: "#ffd39d",
        dark: "#c8872b",
        contrastText: "#140f08",
      },
      background: {
        default: tokens.defaultBackground,
        paper: tokens.paperBackground,
      },
      text: {
        primary: tokens.textPrimary,
        secondary: tokens.textSecondary,
      },
      divider: alpha(tokens.dividerBase, 0.14),
      error: {
        main: "#ff6b81",
      },
      warning: {
        main: "#ffb454",
      },
      info: {
        main: "#6bdfff",
      },
      success: {
        main: "#4cd7a7",
      },
    },
    shape: {
      borderRadius: 16,
    },
    spacing: 8,
    typography: {
      fontFamily: '"IBM Plex Mono", "SFMono-Regular", "Consolas", monospace',
      h4: {
        fontWeight: 700,
        letterSpacing: "-0.03em",
      },
      h5: {
        fontWeight: 700,
        letterSpacing: "-0.02em",
      },
      h6: {
        fontWeight: 600,
      },
      button: {
        fontWeight: 600,
        textTransform: "none",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            minHeight: "100%",
          },
          body: {
            minHeight: "100%",
            background: tokens.bodyBackground,
            color: tokens.bodyColor,
          },
          "#root": {
            minHeight: "100%",
          },
          "@media (hover: hover) and (pointer: fine)": {
            "html, body, body *, body *::before, body *::after, #root, #root *, #root *::before, #root *::after":
              {
                cursor: "none !important",
              },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          variant: "contained",
        },
        styleOverrides: {
          root: {
            minHeight: 44,
            borderRadius: 12,
            paddingInline: 18,
            boxShadow: "none",
          },
          contained: {
            background: `linear-gradient(135deg, ${tokens.buttonContainedStart} 0%, ${tokens.buttonContainedEnd} 100%)`,
            color: tokens.buttonContainedColor,
            "&:hover": {
              boxShadow: "none",
              background: `linear-gradient(135deg, ${tokens.buttonContainedHoverStart} 0%, ${tokens.buttonContainedHoverEnd} 100%)`,
            },
          },
          outlined: {
            borderColor: alpha("#4dd0e1", 0.32),
            "&:hover": {
              borderColor: alpha("#4dd0e1", 0.5),
              backgroundColor: alpha("#4dd0e1", 0.08),
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            border: `1px solid ${alpha(tokens.paperBorderBase, 0.14)}`,
            backgroundColor: alpha(tokens.cardBackground, 0.88),
            backdropFilter: "blur(18px)",
            boxShadow: "0 18px 50px rgba(0, 0, 0, 0.22)",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            border: `1px solid ${alpha(tokens.paperBorderBase, 0.12)}`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            margin: "auto",
            width: "auto",
            minWidth: "500px",
            borderRadius: 15,
            backgroundColor: tokens.dialogBackground,
            backgroundImage: "none",
            border: `1px solid ${alpha(tokens.paperBorderBase, 0.16)}`,
            "@media (max-width:599.95px)": {
              minWidth: "calc(100vw - 32px)",
            },
            "@media (min-width:600px)": {
              width: "auto",
            },
            "@media (min-width:960px)": {
              width: "auto",
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: "24px 24px 8px",
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: "16px 24px",
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "8px 24px 24px",
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          fullWidth: true,
          variant: "outlined",
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            backgroundColor: alpha(
              tokens.inputBackground,
              mode === "light" ? 0.72 : 0.42,
            ),
            transition: "border-color 160ms ease, box-shadow 160ms ease",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(tokens.outlinedInputBorderBase, 0.18),
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha("#4dd0e1", 0.34),
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#4dd0e1",
              boxShadow: `0 0 0 4px ${alpha("#4dd0e1", 0.12)}`,
            },
          },
          input: {
            paddingBlock: 14,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            "&.active, &.Mui-selected": {
              backgroundColor: tokens.listItemActiveBackground,
            },
          },
        },
      },
      MuiContainer: {
        defaultProps: {
          maxWidth: "lg",
        },
      },
    },
  });
};

export const customTheme = createAppTheme("dark");

export type AppTheme = typeof customTheme;
